// Pure helpers that turn a CrewProfile into the strings and visibility
// flags ProfilePage renders. Kept outside the component so the "derives
// display data from a fixture, no crash" acceptance criterion can run
// without mounting anything (see profileDisplay.test.ts).
import type { CrewProfile, EarnedDecoration, StarTier } from '@clan-heart/shared';
import { rateLabel } from '@clan-heart/shared';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "March 2024" from an ISO datetime string — read via the UTC getters so a
 * midnight timestamp never rolls back a day (and so a month) in a timezone
 * behind UTC. */
export function monthYear(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getUTCMonth()] ?? ''} ${d.getUTCFullYear()}`;
}

/** The calendar year (UTC) an ISO datetime string falls in. */
export function yearOf(iso: string): number {
  return new Date(iso).getUTCFullYear();
}

/** "3rd", "11th", "21st" — English ordinal suffix for a 1-based rank. */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const DECK_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six'];

function deckWord(decks: number): string {
  return DECK_WORDS[decks] ?? String(decks);
}

/** "1st rate · four decks · 118 guns" — the meta line under a favourite
 * ship's name (PROTO's `.pf-ship-meta`). */
export function shipMeta(ship: Pick<NonNullable<CrewProfile['favouriteShip']>, 'rate' | 'decks' | 'guns'>): string {
  const deckLabel = `${deckWord(ship.decks)} deck${ship.decks === 1 ? '' : 's'}`;
  return `${rateLabel(ship.rate)} · ${deckLabel} · ${ship.guns} guns`;
}

/** One star-icon modifier per star reached, dimmest (bronze) first — empty
 * for 'none'. Cumulative, so reaching silver shows the bronze icon too. */
export function starIcons(tier: StarTier): Array<'b' | 's' | 'g'> {
  if (tier === 'bronze') return ['b'];
  if (tier === 'silver') return ['b', 's'];
  if (tier === 'gold') return ['b', 's', 'g'];
  return [];
}

const STAR_LABEL: Record<Exclude<StarTier, 'none'>, string> = { bronze: 'Bronze star', silver: 'Silver star', gold: 'Gold star' };

/** The hover card for a ribbon or medal: what it's for, when it was earned,
 * repeats and service stars — only the lines that apply. */
export function decorationDetails(
  ribbon: Pick<EarnedDecoration, 'title' | 'description' | 'firstEarnedAt' | 'count' | 'stars'>,
): string[] {
  const lines: string[] = [];
  const description = ribbon.description.trim();
  // a description that only repeats the name adds nothing to the card
  if (description && description.toLowerCase() !== ribbon.title.trim().toLowerCase()) lines.push(description);
  lines.push(`Earned ${monthYear(ribbon.firstEarnedAt)}`);
  if (ribbon.count > 1) lines.push(`Earned ${ribbon.count} times`);
  const { tier, next } = ribbon.stars;
  if (tier !== 'none') lines.push(next !== null ? `${STAR_LABEL[tier]} · next at ${next}` : STAR_LABEL[tier]);
  else if (next !== null) lines.push(`First star at ${next}`);
  return lines;
}

/** The hero's motto line is italic text under the name/rank — shown only
 * when the member actually set one; trims so whitespace-only never renders
 * an empty line. */
export function mottoDisplay(motto: string): string | null {
  const trimmed = motto.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Pure: whether a loaded profile's canonical slug differs from the one in
 * the URL, and if so, the path to replace the URL with. A rename moves the
 * slug (see `syncProfileSlug` in api/src/profiles/service.ts) and old links
 * keep working by following this redirect, not by erroring.
 */
export function canonicalRedirectPath(urlSlug: string, profile: Pick<CrewProfile, 'slug'>): string | null {
  return profile.slug !== urlSlug ? `/crew/${profile.slug}` : null;
}

const STAR_WEIGHT: Record<StarTier, number> = { none: 0, bronze: 1, silver: 2, gold: 3 };

function totalServiceStars(ribbons: readonly EarnedDecoration[]): number {
  return ribbons.reduce((sum, r) => sum + STAR_WEIGHT[r.stars.tier], 0);
}

export interface ProfileShipDisplay {
  name: string;
  meta: string;
  planImage: string | null;
  planCredit: string;
}

/** Everything ProfilePage derives from one CrewProfile before rendering. */
export interface ProfileDisplay {
  sinceLabel: string;
  leaderboardLabel: string;
  motto: string | null;
  ship: ProfileShipDisplay | null;
  flagshipQuote: string | null;
  showMedals: boolean;
  showRibbons: boolean;
  showServiceStarNote: boolean;
  showEnsigns: boolean;
  showCollection: boolean;
  showClosest: boolean;
  medalsSummary: string;
  ribbonsSummary: string;
  ensignsSummary: string;
  collectionSummary: string;
}

/** Pure: derives every display string/flag ProfilePage needs from a
 * CrewProfile. Handles every "nothing here" edge (no favourite ship, no
 * flagship name, no decorations of a kind, an empty closestToEarning)
 * without throwing. */
export function deriveProfileDisplay(profile: CrewProfile): ProfileDisplay {
  const ship = profile.favouriteShip
    ? {
        name: profile.favouriteShip.name,
        meta: shipMeta(profile.favouriteShip),
        planImage: profile.favouriteShip.plan?.image ?? null,
        planCredit: profile.favouriteShip.plan?.credit ?? '',
      }
    : null;

  const flagshipQuote = profile.flagshipName ? `“${profile.flagshipName}”` : null;

  const stars = totalServiceStars(profile.ribbons);
  const ribbonsCount = profile.ribbons.length;

  return {
    sinceLabel: `Aboard since ${monthYear(profile.joinedAt)}`,
    leaderboardLabel: ordinal(profile.stats.leaderboardPosition),
    motto: mottoDisplay(profile.motto),
    ship,
    flagshipQuote,
    showMedals: profile.medals.length > 0,
    showRibbons: ribbonsCount > 0,
    showServiceStarNote: profile.ribbons.some((r) => r.stars.tier !== 'none' || r.stars.next !== null),
    showEnsigns: profile.ensigns.length > 0,
    showCollection: profile.collection.total > 0,
    showClosest: profile.closestToEarning.length > 0,
    medalsSummary: `${profile.medals.length} earned`,
    ribbonsSummary:
      stars > 0 ? `${ribbonsCount} earned · ${stars} service star${stars === 1 ? '' : 's'}` : `${ribbonsCount} earned`,
    ensignsSummary: `${profile.ensigns.length} campaign${profile.ensigns.length === 1 ? '' : 's'}`,
    collectionSummary: `${profile.collection.earned} of ${profile.collection.total} decorations`,
  };
}
